import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import clsx from 'clsx';
import {
  makeStyles,
  withStyles,
  Box,
  Slider,
  Tooltip,
} from '@material-ui/core';
import { OfflinePin } from '@material-ui/icons';
import PropTypes from 'prop-types';
import { AriaLiveRegion } from 'components';
import {
  NextAndBackButtons,
  PageNumberInput,
} from 'components/ScrubBar/components';
import {
  getMarksForBook,
  getNewPageForPercentage,
  getStablePageLocation,
} from 'components/ScrubBar/components/ScrubBarHelpers';
import {
  BookValuesContext,
  BookLocationValuesContext,
  DocumentValuesContext,
  NetworkStatusValuesContext,
  OfflineValuesContext,
} from 'contexts';
import { useKeyboardShortcut } from 'hooks';
import { getDocumentForPercentageOfBook } from 'utilities/locationLogic';
import { BookLocation } from 'types';

const useStyles = makeStyles((theme) => ({
  scrubbar__navigation: {
    display: 'flex',
    marginLeft: '12px',
  },
  scrubbar_wrapper: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    padding: theme.spacing(0, 2.25),

    borderRadius: theme.spacing(3),
    outlineOffset: theme.spacing(0.25),
    outline: ({ isThumbActive }) => (isThumbActive ? '2px solid grey' : 'none'),
  },
  scrubbar__slider: {
    display: 'flex',
    justifyContent: 'center',
    gridTemplateColumns: '1fr auto',
    gap: theme.spacing(3.75),
    alignItems: 'center',

    '& .MuiSlider-rail': {
      backgroundColor: ({ isThumbActive, isThumbHovered }) =>
        isThumbActive || isThumbHovered
          ? theme.palette.common.white
          : '#B9DCFE',
    },

    '& .MuiSlider-thumb': {
      backgroundColor: ({ isThumbActive, isThumbHovered }) =>
        isThumbActive || isThumbHovered
          ? '#B9DCFE'
          : theme.palette.common.white,
    },

    [theme.breakpoints.down('xs')]: {
      '& .MuiSlider-root': {
        marginRight: '12px',
      },
    },
  },

  tooltip__page_number_Wrapper: {
    display: 'flex',
    flexDirection: 'row',
  },
  tooltip__page_number: {
    ...theme.typography.body1,
    fontSize: 16,
    lineHeight: '24px',
    fontWeight: 500,
    color: theme.palette.greyBlue[700],
    paddingRight: '4px',
  },
  tooltip__chapter: {
    ...theme.typography.body2,
    fontSize: 14,
    lineHeight: '21px',
    fontWeight: 400,
    color: theme.palette.greyBlue[700],
  },
  tooltip__offline_not_available: {
    color: '#9E9E9E',
  },
}));

const ScrubBarSlider = withStyles((theme) => {
  const outlineStyle = `2px solid ${theme.palette.grey[700]}`;
  const thumbInteractionStyle = {
    boxShadow: 'none',
    outline: outlineStyle,
    backgroundColor: '#B9DCFE',
  };

  return {
    root: {
      maxWidth: '100%',
      padding: theme.spacing(2.875, 0),
      position: 'relative',
    },
    thumb: {
      height: theme.spacing(1.5),
      width: theme.spacing(1.5),
      marginTop: theme.spacing(-0.75),
      marginLeft: theme.spacing(-0.75),
      border: outlineStyle,

      transition: 'backgroundColor 0.2s',
      outlineOffset: theme.spacing(1.875),
      '&:hover': thumbInteractionStyle,
      '&:active': thumbInteractionStyle,
      '&:focus': thumbInteractionStyle,
    },
    track: {
      height: 4,
      borderRadius: 4,
      transform: 'translateY(-1px)',
      backgroundColor: theme.palette.shelfBlue[500],
      transition: 'backgroundColor 0.2s',
    },
    rail: {
      height: theme.spacing(0.75),
      width: `calc(100% + 8px)`, // mark + border + negative left
      backgroundColor: 'white',
      border: `1px solid ${theme.palette.grey[700]}`,
      borderRadius: theme.spacing(0.5),
      transition: 'backgroundColor 0.2s',
      top: theme.spacing(2.625),
      left: theme.spacing(-0.25),
      opacity: 1,
    },
    mark: {
      height: 4,
      width: 4,
      backgroundColor: theme.palette.shelfBlue[500],
      borderRadius: '50%',
      opacity: 1,
      marginTop: -1,
    },
    markLabel: {
      [theme.breakpoints.down('sm')]: {
        opacity: 0,
        transition: 'opacity 0.2s',
      },
    },
    marked: {
      marginBottom: 0,
    },
    markActive: {
      backgroundColor: theme.palette.common.white,
    },
  };
})(Slider);

const ScrubbarTooltip = withStyles((theme) => {
  return {
    tooltip: {
      backgroundColor: '#ffffff',
      color: '#000000',
      width: 296,
      height: 60,
      marginLeft: '6px',
      maxWidth: '90%',
      border: '1px solid #D8DEE4',
      borderRadius: 4,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      boxShadow: '0 4px 4px rgba(0, 0, 0, 0.4)',
      padding: '8px',
    },
    popper: {
      [theme.breakpoints.up('sm')]: {
        top: '-12px !important',
      },
    },
  };
})(Tooltip);

/**
 * Wrapper for the ScrubBar tooltip (MUI calls it ValueLabelComponent)
 */
const ValueLabelComponent = ({ children, open, value }) => {
  return (
    <Box elevation={1}>
      <ScrubbarTooltip
        enterTouchDelay={0}
        open={open}
        placement="top"
        title={value}
      >
        {children}
      </ScrubbarTooltip>
    </Box>
  );
};

/**
 * Content inside the ScrubBar tooltip (MUI calls it valueLabelFormat)
 */
const ValueLabelContent = ({ classes, isDownloaded, isOffline, page }) => {
  const pageLabel = page?.label?.toString();
  const markerLabel = page?.marker?.label?.toString();
  const dimClassName =
    isOffline && !isDownloaded ? classes.tooltip__offline_not_available : null;

  return (
    <>
      {pageLabel ? (
        <span className={classes.tooltip__page_number_Wrapper}>
          <span className={clsx(classes.tooltip__page_number, dimClassName)}>
            Page <span className="noTranslate">{pageLabel}</span>
          </span>
          {isDownloaded && <OfflinePin style={{ color: '#4E5F6F' }} />}
        </span>
      ) : null}
      {markerLabel ? (
        <span
          className={clsx(
            classes.tooltip__chapter,
            dimClassName,
            'noTranslate',
          )}
        >
          {markerLabel.length > 35
            ? markerLabel.substring(0, 30) + '...'
            : markerLabel}
        </span>
      ) : null}
    </>
  );
};

/**
 * Slider component for the Reader
 */
const ReaderSlider = ({
  isMobile,
  isMobileScrollingView,
  onChooseNewLocation,
  ...props
}) => {
  const [isThumbActive, setIsThumbActive] = useState(false);
  const [isThumbHovered, setIsThumbHovered] = useState(false);

  const classes = useStyles({ isThumbActive, isThumbHovered });

  const { book } = useContext(BookValuesContext);
  const { currentBookLocation } = useContext(BookLocationValuesContext);
  const { frame } = useContext(DocumentValuesContext);
  const { isOffline } = useContext(NetworkStatusValuesContext);
  const { checkIsChapterDownloaded } = useContext(OfflineValuesContext);

  const setValueLabelDisplayToAutoTimeOut = useRef(); // ref with timeout for setting valueLabelDisplay to auto
  const [valueLabelDisplay, setValueLabelDisplay] = useState('auto'); // display state for ValueLabelComponent/sliderTooltip (auto, on, off)
  const [ariaMsgForSliderTooltip, setAriaMsgForSliderTooltip] = useState(); // message for aria live region to announce page Info in tooltip
  const [pageAndPercent, setPageAndPercent] = useState({
    // page and percentage for current location
    page: currentBookLocation?.page,
    percentage: currentBookLocation?.percentageOfBook ?? 0,
  });

  useEffect(
    function updatePageAndPercentForCurrentBookLocation() {
      setPageAndPercent((prevState) => {
        const nextPct =
          currentBookLocation?.percentageOfBook ?? prevState.percentage;
        // Page: from location, or resolve from % when location has no page yet, or keep prev
        const nextPage =
          currentBookLocation?.page ??
          (currentBookLocation?.percentageOfBook != null &&
          book &&
          Number.isFinite(currentBookLocation.percentageOfBook)
            ? getNewPageForPercentage(
                book,
                currentBookLocation.percentageOfBook,
              )
            : null) ??
          prevState.page;

        return { page: nextPage, percentage: nextPct };
      });
    },
    [
      currentBookLocation?.page,
      currentBookLocation?.percentageOfBook,
      currentBookLocation?.document?.index,
      book,
    ],
  );

  const { page, percentage } = pageAndPercent;
  const canNavigate = Boolean(book);
  const isDownloaded = checkIsChapterDownloaded(page?.marker);

  const marks = useMemo(() => getMarksForBook(book), [book]); // marks need to change only when book changes

  // Next/previous doc from displayed position (so offline matches online: each press advances doc + page).
  // Prefer currentBookLocation.document when present (page can belong to multiple docs and page.document is the first).
  const { nextLocation, previousLocation } = useMemo(() => {
    if (!book) return { nextLocation: null, previousLocation: null };
    const doc =
      currentBookLocation?.document ??
      getDocumentForPercentageOfBook(book, percentage) ??
      page?.document;
    if (!doc) return { nextLocation: null, previousLocation: null };
    const nextDoc = doc.next;
    const prevDoc = doc.previous;
    return {
      nextLocation: nextDoc
        ? new BookLocation({
            book,
            document: nextDoc,
            percentageOfDocument: 0,
          })
        : null,
      previousLocation: prevDoc
        ? new BookLocation({
            book,
            document: prevDoc,
            percentageOfDocument: 1,
          })
        : null,
    };
  }, [book, currentBookLocation?.document, page, percentage]);

  // Optimistic update when navigating (so offline shows new page like online even if doc does not load).
  const handleChooseNewLocation = useCallback(
    async (newLocation, callback = () => {}) => {
      if (newLocation) {
        const newPage = newLocation.page ?? null;
        const newPercentage =
          newLocation.percentageOfBook ?? newPage?.percentageStart ?? undefined;
        if (
          newPage != null ||
          (newPercentage != null && !Number.isNaN(newPercentage))
        ) {
          const pct = Number.isFinite(newPercentage)
            ? newPercentage
            : undefined;
          const resolvedPage =
            newPage ??
            (book && pct != null ? getNewPageForPercentage(book, pct) : null);
          setPageAndPercent((prev) => ({
            page: resolvedPage ?? prev.page,
            percentage: pct ?? prev.percentage,
          }));
        }
      }
      await onChooseNewLocation(newLocation, callback);
    },
    [book, onChooseNewLocation],
  );

  useKeyboardShortcut(
    ['ctrl+alt+/', 'ctrl+command+/'],
    () => {
      setValueLabelDisplay('on');
      setAriaMsgForSliderTooltip(
        `Page ${page?.label} ${page?.marker?.label ?? ''}`,
      );
    },
    {
      event: 'keydown',
    },
  );

  useEffect(
    function setValueLabelDisplayToAuto() {
      if (setValueLabelDisplayToAutoTimeOut.current) {
        clearTimeout(setValueLabelDisplayToAutoTimeOut.current);
      }

      if (valueLabelDisplay === 'auto') {
        setAriaMsgForSliderTooltip(null);
        return;
      }

      setValueLabelDisplayToAutoTimeOut.current = setTimeout(() => {
        setValueLabelDisplay('auto');
        setAriaMsgForSliderTooltip(null);
      }, 3000);
    },
    [valueLabelDisplay, page],
  );

  // user moves the slider
  const handleSliderChange = (event, newPercentageTimesTenThousand) => {
    // Firefox needs this to announce tooltip on VO for arrow navigation correctly
    setAriaMsgForSliderTooltip(null);

    // prevent conflicts with handling ArrowLeft and ArrowRight keyboard navigation
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const newPercentage = newPercentageTimesTenThousand / 10000;
    const newPage = getNewPageForPercentage(book, newPercentage);

    setPageAndPercent({
      page: newPage,
      percentage: newPercentage,
    });
  };

  // user commits the slider change (releases the slider)
  const handleSliderChangeCommitted = (
    event,
    newPercentageTimesTenThousand,
  ) => {
    // prevent conflicts with handling ArrowLeft and ArrowRight keyboard navigation
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    // Slider value is scaled by 10000 for precision; convert back to 0–1 for BookLocation
    const newPercentage = newPercentageTimesTenThousand / 10000;
    handleChooseNewLocation(
      new BookLocation({
        book: book,
        event: 'Scrub bar slider change',
        percentageOfBook: newPercentage,
      }),
    );
  };

  // Firefox tries to catch keydown events
  const preventPropagation = (event) => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  // left and right arrow keyboard navigation
  const handleKeyboardNavigation = (event) => {
    preventPropagation(event);

    if (event.key === 'ArrowRight') {
      const next = book.pages[currentBookLocation?.pageIndex + 1];
      const nextDocument =
        next?.document ??
        book.documents[currentBookLocation?.documentIndex + 1];

      if (!next || !nextDocument) return;

      const isLocationInSameDocument =
        nextDocument.index === currentBookLocation?.documentIndex &&
        currentBookLocation?.percentageOfDocument < 1;

      // if document of next page and current location doesn't match or if we have reached the end of document,
      // we can just move to top of next document
      // if we are in the same document, get stable bookLocation for page
      const nextPageLocation = isLocationInSameDocument
        ? getStablePageLocation(frame, next, currentBookLocation)
        : new BookLocation({
            book: book,
            document: book.documents[currentBookLocation?.documentIndex + 1],
            percentageOfDocument: 0,
          });

      handleChooseNewLocation(nextPageLocation);
    }

    if (event.key === 'ArrowLeft') {
      const previous = book.pages[currentBookLocation?.pageIndex - 1];
      const previousDocument =
        previous?.document ??
        book.documents[currentBookLocation?.documentIndex - 1];

      if (!previous || !previousDocument) return;

      const isLocationInSameDocument =
        previousDocument.index === currentBookLocation?.documentIndex &&
        currentBookLocation?.percentageOfDocument > 0;

      // if document of previous page and current location doesn't match or if we are at the start of document,
      // we can just move to bottom of previous document
      // if we are in the same document, get stable bookLocation for page
      const previousPageLocation = isLocationInSameDocument
        ? getStablePageLocation(frame, previous, currentBookLocation)
        : new BookLocation({
            book: book,
            document: book.documents[currentBookLocation?.documentIndex - 1],
            percentageOfDocument: 1,
          });

      handleChooseNewLocation(previousPageLocation);
    }
  };

  return (
    <>
      <div className={classes.scrubbar__navigation}>
        <NextAndBackButtons
          canNavigate={canNavigate}
          nextLocation={nextLocation}
          onChooseNewLocation={handleChooseNewLocation}
          previousLocation={previousLocation}
          setAriaMsgForSliderTooltip={setAriaMsgForSliderTooltip}
          setValueLabelDisplay={setValueLabelDisplay}
        />
      </div>
      <div className={classes.scrubbar__slider}>
        {!isMobile ? (
          <div className={classes.scrubbar_wrapper}>
            <ScrubBarSlider
              aria-label="pages slider"
              aria-valuetext={
                !page?.marker?.label
                  ? `Page ${page?.label}`
                  : `Page ${page?.label} ${page?.marker?.label}`
              } // announced by the screen reader whenever the ValueLabelComponent is visible and the location is changed from the slider (keyboard navigation, mouse drag and drop or click)
              disabled={!canNavigate || isMobileScrollingView}
              marks={marks}
              max={10000}
              min={0}
              onChange={handleSliderChange}
              onChangeCommitted={handleSliderChangeCommitted}
              onKeyDown={(e) => preventPropagation(e)} // Firefox tries to catch keydown events
              onKeyUp={handleKeyboardNavigation}
              onFocus={() => setIsThumbActive(true)}
              onBlur={() => setIsThumbActive(false)}
              onMouseEnter={() => setIsThumbHovered(true)}
              onMouseLeave={() => setIsThumbHovered(false)}
              value={percentage * 10000}
              ValueLabelComponent={ValueLabelComponent}
              valueLabelDisplay={valueLabelDisplay}
              valueLabelFormat={() => {
                return (
                  <ValueLabelContent
                    classes={classes}
                    isDownloaded={isDownloaded}
                    isOffline={isOffline}
                    page={page}
                  />
                );
              }}
            />
          </div>
        ) : null}
        <AriaLiveRegion
          id={'scrub-bar-tooltip-page-info'}
          message={ariaMsgForSliderTooltip}
        />
        <PageNumberInput
          canNavigate={canNavigate}
          onChooseNewLocation={handleChooseNewLocation}
          page={page}
          setValueLabelDisplay={setValueLabelDisplay}
        />
      </div>
    </>
  );
};

ReaderSlider.propTypes = {
  isMobile: PropTypes.bool,
  isMobileScrollingView: PropTypes.bool,
  onChooseNewLocation: PropTypes.func,
};

export default ReaderSlider;
